import type Post from '@/models/post';
import { createPost } from '@/api/itinerary';
import type {
  CityFormData,
  GeoJSONPoint,
  PrimaryDestination,
  PrimaryDestinationType,
} from '@/types/city';
import type { PlaceCardData } from '@/types/listViewTypes';
import type {
  ItineraryImportDraft,
  ItineraryImportDraftPlace,
  ItineraryImportSourceType,
} from '@/models/itineraryImportTypes';
import { isSocialItineraryImportSourceType } from '@/models/itineraryImportTypes';
import { mongoObjectIdStringOrNull } from '@/utils/idGeneration';
import { resolveGtCategory, type GtCategory } from '@/utils/gtCategory';
import { getPhotoUrls, type PhotoIdentifier } from '@/utils/photoUtils';
import type { SocialImportPlace } from '@/utils/inspoImportBuilder';
import {
  type SourceMetadata,
  buildItineraryPostObject,
} from '@/utils/postBuilder';
import type { CreatePostPayload, ItinerarySlot } from '@/utils/itinerary/types';

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_REGEX = /(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{2,4}\)|\d{2,4})[\s.-]?\d{3,4}[\s.-]?\d{3,4}/g;

export function scrubContactDetails(text: string): string {
  return text.replace(EMAIL_REGEX, '').replace(PHONE_REGEX, '').replace(/\s{2,}/g, ' ').trim();
}

export function maybeScrubImportText(
  text: string,
  sourceType: ItineraryImportSourceType,
): string {
  if (isSocialItineraryImportSourceType(sourceType)) {
    return text.trim();
  }
  return scrubContactDetails(text);
}

export function normalizeTimeRange(raw: string | null | undefined): string | undefined {
  if (!raw || !raw.trim()) return undefined;
  const trimmed = raw.trim();
  const rangeMatch = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/.exec(trimmed);
  if (rangeMatch) {
    const pad = (t: string) => {
      const [h, m] = t.split(':');
      return `${h.padStart(2, '0')}:${m}`;
    };
    return `${pad(rangeMatch[1])}-${pad(rangeMatch[2])}`;
  }
  const pointMatch = /^(\d{1,2}:\d{2})$/.exec(trimmed);
  if (pointMatch) {
    const [h, m] = pointMatch[1].split(':');
    return `${h.padStart(2, '0')}:${m}-${h.padStart(2, '0')}:${m}`;
  }
  return undefined;
}

function toPhotoIdentifiers(photos?: string[]): PhotoIdentifier[] {
  return (photos ?? []).map((photo) => ({ name: photo }));
}

function slotTypeFromCategory(category: GtCategory | null | undefined): ItinerarySlot['slotType'] {
  if (category === 'eat' || category === 'stay' || category === 'do') {
    return category;
  }
  return 'do';
}

function importSlotType(
  place: ItineraryImportDraftPlace,
  category: GtCategory | null | undefined,
): ItinerarySlot['slotType'] {
  if (place.row_kind === 'travel') return 'travel';
  return slotTypeFromCategory(category);
}

const PRIMARY_DESTINATION_TYPES: PrimaryDestinationType[] = ['city', 'locality', 'country'];

function isPrimaryDestinationType(value: unknown): value is PrimaryDestinationType {
  return typeof value === 'string' && PRIMARY_DESTINATION_TYPES.includes(value as PrimaryDestinationType);
}

function normalizeImportDestination(
  destination: ItineraryImportDraft['destination'],
): PrimaryDestination | null {
  if (!destination?.identifying_name?.trim()) return null;
  if (!isPrimaryDestinationType(destination.type)) return null;
  const identifying_name = destination.identifying_name.trim();
  return {
    type: destination.type,
    identifying_name,
    ...(destination.google_place_id ? { google_place_id: destination.google_place_id } : {}),
    ...(destination.geoposition ? { geoposition: destination.geoposition } : {}),
  };
}

function cityFormDataFromPrimaryDestination(primary: PrimaryDestination): CityFormData {
  const identifying = primary.identifying_name.trim();
  const [cityPart] = identifying.split(',').map((s) => s.trim());
  const city = cityPart || identifying;
  const cityIdentifyingName = identifying.includes(',') ? identifying : city;
  return {
    city,
    name: city,
    city_identifying_name: cityIdentifyingName,
    a3_intl_address: cityIdentifyingName,
    ...(primary.google_place_id ? { google_place_id: primary.google_place_id } : {}),
    ...(primary.geoposition ? { geoposition: primary.geoposition as GeoJSONPoint } : {}),
  };
}

function looksLikeStreetAddress(segment: string): boolean {
  const trimmed = segment.trim();
  if (!trimmed) return true;
  if (/^\d+$/.test(trimmed)) return true;
  if (/^\d{4,5}\s+[A-Za-zÀ-ÿ]/.test(trimmed)) return false;
  if (/^\d+\s/.test(trimmed)) return true;
  return /^(via|viale|piazza|corso|strada|route|rue|calle|avenue|ave\.?|street|st\.?|road|rd\.?|boulevard|blvd\.?)\b/i.test(
    trimmed,
  );
}

/** Best-effort city/locality label from a Google-style comma address. */
export function extractLocalityFromImportAddress(address: string | undefined): string | null {
  if (!address?.trim()) return null;
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;

  const country = parts[parts.length - 1];
  const beforeCountry = parts[parts.length - 2];
  if (!beforeCountry || looksLikeStreetAddress(beforeCountry)) {
    return null;
  }

  const postalLocality = beforeCountry.match(/^(?:\d{4,5}\s+)?(.+?)(?:\s+[A-Z]{2})?$/);
  const locality = (postalLocality?.[1] ?? beforeCountry).trim();
  if (!locality || looksLikeStreetAddress(locality)) return null;
  if (locality.toLowerCase() === country.toLowerCase()) return null;
  return locality;
}

function dominantCountryFromDraftPlaces(draft: ItineraryImportDraft): string | null {
  const counts = new Map<string, number>();
  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      const address = place.location?.address?.trim();
      if (!address) return;
      const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) return;
      const country = parts[parts.length - 1];
      if (!country) return;
      counts.set(country, (counts.get(country) ?? 0) + 1);
    });
  });
  let best: string | null = null;
  let bestCount = 0;
  counts.forEach((count, country) => {
    if (count > bestCount) {
      best = country;
      bestCount = count;
    }
  });
  return best;
}

function inferDestinationFromImportPlaces(draft: ItineraryImportDraft): PrimaryDestination | null {
  const localityCounts = new Map<string, number>();
  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      const locality = extractLocalityFromImportAddress(place.location?.address);
      if (!locality) return;
      localityCounts.set(locality, (localityCounts.get(locality) ?? 0) + 1);
    });
  });
  if (localityCounts.size === 0) return null;

  let bestLocality: string | null = null;
  let bestCount = 0;
  localityCounts.forEach((count, locality) => {
    if (count > bestCount) {
      bestLocality = locality;
      bestCount = count;
    }
  });
  if (!bestLocality) return null;

  const country = dominantCountryFromDraftPlaces(draft);
  const identifying_name = country ? `${bestLocality}, ${country}` : bestLocality;
  return { type: 'city', identifying_name };
}

const TITLE_DESTINATION_SKIP = /^(from|google|imported|trip|itinerary|draft)\b/i;

/** e.g. "Honeymoon on the Amalfi Coast" → Amalfi Coast */
export function inferDestinationLabelFromImportTitle(title: string | null | undefined): string | null {
  const trimmed = title?.trim();
  if (!trimmed || TITLE_DESTINATION_SKIP.test(trimmed)) return null;
  const match = trimmed.match(/\b(?:on|in|to|through)\s+(?:the\s+)?([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'-]{2,})\s*$/i);
  const label = match?.[1]?.trim();
  if (!label || label.length < 3) return null;
  if (/^(day|week|night|days|nights)\b/i.test(label)) return null;
  return label;
}

function inferDestinationFromImportTitle(
  draft: ItineraryImportDraft,
): PrimaryDestination | null {
  const label = inferDestinationLabelFromImportTitle(draft.title);
  if (!label) return null;
  const country = dominantCountryFromDraftPlaces(draft);
  const identifying_name = country && !label.includes(',') ? `${label}, ${country}` : label;
  return { type: 'locality', identifying_name };
}

/** Resolve trip destination for create: API field, then title, then place addresses. */
export function resolveImportDraftDestination(draft: ItineraryImportDraft): {
  primaryDestination: PrimaryDestination | null;
  city: CityFormData | null;
} {
  const fromApi = normalizeImportDestination(draft.destination);
  if (fromApi) {
    return { primaryDestination: fromApi, city: cityFormDataFromPrimaryDestination(fromApi) };
  }
  const fromTitle = inferDestinationFromImportTitle(draft);
  if (fromTitle) {
    return { primaryDestination: fromTitle, city: cityFormDataFromPrimaryDestination(fromTitle) };
  }
  const fromPlaces = inferDestinationFromImportPlaces(draft);
  if (fromPlaces) {
    return { primaryDestination: fromPlaces, city: cityFormDataFromPrimaryDestination(fromPlaces) };
  }
  return { primaryDestination: null, city: null };
}

/** Destination fields for `createListWithPlace` — mirrors trip import itinerary create. */
export function listCreateDestinationOptionsFromDraft(draft: ItineraryImportDraft): {
  primaryDestination: PrimaryDestination | null;
  cityIdentifyingNames: string[];
} {
  const { primaryDestination, city } = resolveImportDraftDestination(draft);
  const cityIdentifyingNames =
    city?.city_identifying_name?.trim()
      ? [city.city_identifying_name.trim()]
      : primaryDestination?.identifying_name?.trim()
        ? [primaryDestination.identifying_name.trim()]
        : [];
  return { primaryDestination, cityIdentifyingNames };
}

function dayLabelToNote(label: string | null | undefined, dayIndex: number): string | undefined {
  const trimmed = label?.trim();
  if (!trimmed) return undefined;
  const titledDay = trimmed.match(/^Day\s+\d+\s*[—-]\s*(.+)$/i);
  if (titledDay?.[1]?.trim()) {
    return titledDay[1].trim();
  }
  const dayNumberPattern = new RegExp(`^Day\\s+${dayIndex + 1}\\b`, 'i');
  if (dayNumberPattern.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function mapPlaceToSlot(
  place: ItineraryImportDraftPlace,
  dayIndex: number,
  sequenceIndex: number,
  sourceType: ItineraryImportSourceType,
): ItinerarySlot {
  const notes = place.notes ? maybeScrubImportText(place.notes, sourceType) : undefined;
  const timeRange = normalizeTimeRange(place.time);

  // Travel is a custom stop. A draft location, including a stray Google id, is not saved.
  if (place.row_kind !== 'travel' && place.location) {
    const loc = place.location;
    const gtCategory =
      (loc.gt_category as GtCategory | undefined) ??
      (place.category as GtCategory | undefined) ??
      resolveGtCategory(loc);
    const photoUrls = getPhotoUrls(toPhotoIdentifiers(loc.photos));
    const activityName = place.name.trim() || loc.name || 'Place';
    return {
      dayIndex,
      sequenceIndex,
      name: activityName,
      notes,
      timeRange,
      slotType: importSlotType(place, gtCategory),
      placeId: loc.google_place_id,
      locationData: {
        _id: loc._id,
        name: loc.name ?? activityName,
        google_place_id: loc.google_place_id,
        gt_category: gtCategory,
        geoposition: loc.geoposition,
        a3_intl_address: loc.address,
      },
      image_urls: photoUrls,
      address: loc.address,
    };
  }

  return {
    dayIndex,
    sequenceIndex,
    name: place.name,
    notes,
    timeRange,
    isUserCreated: true,
    slotType: importSlotType(place, place.category as GtCategory | undefined),
    // toPostLocation requires locationData; minimal stub so custom import rows are not dropped on create.
    locationData: {
      name: place.name,
      is_user_created: true,
    },
  };
}

export function buildItinerarySlotsFromDraft(
  draft: ItineraryImportDraft,
  selectedClientIds: Set<string>,
  sourceType: ItineraryImportSourceType = 'text',
): { slots: ItinerarySlot[]; timelineData: Array<{ travelDay?: string; dayNote?: string }> } {
  const slots: ItinerarySlot[] = [];
  const timelineData: Array<{ travelDay?: string; dayNote?: string }> = [];
  const dayCount = Math.max(draft.day_count, draft.days.length, 1);
  const lastDayIndex = Math.max(0, dayCount - 1);

  for (let i = 0; i < dayCount; i += 1) {
    const day = draft.days.find((d) => d.day_index === i) ?? draft.days[i];
    const dayNoteParts: string[] = [];
    const labelNote = dayLabelToNote(day?.label, i);
    if (labelNote) dayNoteParts.push(labelNote);
    if (day?.notes?.trim()) {
      dayNoteParts.push(maybeScrubImportText(day.notes.trim(), sourceType));
    }
    timelineData.push({
      travelDay: `Day ${i + 1}`,
      dayNote: dayNoteParts.length > 0 ? dayNoteParts.join('\n') : undefined,
    });
  }

  draft.days.forEach((day) => {
    let dayIndex = day.day_index;
    if (dayIndex < 0 || dayIndex > lastDayIndex) {
      dayIndex = lastDayIndex;
    }
    let sequenceIndex = 0;
    day.places.forEach((place) => {
      if (!selectedClientIds.has(place.client_id)) return;
      slots.push(mapPlaceToSlot(place, dayIndex, sequenceIndex, sourceType));
      sequenceIndex += 1;
    });
  });

  return { slots, timelineData };
}

export function buildImportSourceMetadata(
  draft: ItineraryImportDraft,
  sourceType: ItineraryImportSourceType,
  importId: string,
  importStartedAt: string,
): SourceMetadata {
  return {
    source: 'itinerary-import',
    source_type: sourceType,
    import_id: importId,
    import_started_at: importStartedAt,
    ...(draft.content_hash ? { content_hash: draft.content_hash } : {}),
    ...(draft.prompt_version ? { prompt_version: draft.prompt_version } : {}),
  };
}

export interface ImportPublishTiming {
  import_id: string;
  import_source_type: ItineraryImportSourceType;
  time_to_storefront_seconds: number;
}

export function getImportPublishTiming(post: Post): ImportPublishTiming | null {
  const meta = (post as { source_metadata?: Record<string, unknown> }).source_metadata;
  if (!meta || meta.source !== 'itinerary-import') return null;
  const importId = typeof meta.import_id === 'string' ? meta.import_id : null;
  const startedAt = typeof meta.import_started_at === 'string' ? meta.import_started_at : null;
  const sourceType = meta.source_type;
  if (!importId || !startedAt) return null;
  if (
    sourceType !== 'text'
    && sourceType !== 'pdf'
    && sourceType !== 'docx'
    && sourceType !== 'instagram'
    && sourceType !== 'tiktok'
  ) {
    return null;
  }
  const startMs = Date.parse(startedAt);
  if (Number.isNaN(startMs)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - startMs) / 1000));
  return {
    import_id: importId,
    import_source_type: sourceType,
    time_to_storefront_seconds: seconds,
  };
}

export function countDraftPlaces(draft: ItineraryImportDraft): number {
  return draft.days.reduce((total, day) => total + day.places.length, 0);
}

export function countTravelRowsInDraft(draft: ItineraryImportDraft): number {
  return draft.days.reduce(
    (total, day) => total + day.places.filter((place) => place.row_kind === 'travel').length,
    0,
  );
}

export function summarizeImport(
  draft: ItineraryImportDraft,
  selectedClientIds: Set<string>,
): {
  dayCount: number;
  placeCount: number;
  bookableCount: number;
  lowConfidenceCount: number;
  unmatchedCount: number;
  redactionCount: number;
  hasDates: boolean;
} {
  let placeCount = 0;
  let bookableCount = 0;
  let lowConfidenceCount = 0;
  let unmatchedCount = 0;

  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      if (!selectedClientIds.has(place.client_id)) return;
      placeCount += 1;
      if (!place.location) {
        unmatchedCount += 1;
        return;
      }
      const cat = place.category ?? place.location.gt_category;
      if (cat === 'stay' || cat === 'do') {
        bookableCount += 1;
      }
      if (place.match_confidence === 'low') {
        lowConfidenceCount += 1;
      }
    });
  });

  const red = draft.redactions;
  const redactionCount = red
    ? red.emails + red.phones + red.prices + red.names + red.booking_refs
    : 0;

  return {
    dayCount: draft.day_count,
    placeCount,
    bookableCount,
    lowConfidenceCount,
    unmatchedCount,
    redactionCount,
    hasDates: Boolean(draft.start_date_string && draft.end_date_string),
  };
}

export async function createItineraryFromImportDraft(params: {
  draft: ItineraryImportDraft;
  selectedClientIds: Set<string>;
  sourceType: ItineraryImportSourceType;
  importId: string;
  importStartedAt: string;
  tripTitle?: string;
}): Promise<Post> {
  const { draft, selectedClientIds, sourceType, importId, importStartedAt, tripTitle: tripTitleOverride } =
    params;
  const { slots, timelineData } = buildItinerarySlotsFromDraft(draft, selectedClientIds, sourceType);
  const { primaryDestination, city } = resolveImportDraftDestination(draft);
  const tripTitle = tripTitleOverride?.trim() || draft.title?.trim() || 'Imported trip';
  const itineraryNotes = draft.summary ? maybeScrubImportText(draft.summary, sourceType) : '';
  const sourceSocialMediaPostId =
    mongoObjectIdStringOrNull(draft.social?.post_id) ?? undefined;
  const actualDayCount = Math.max(draft.day_count, draft.days.length, 1);

  const formData: Record<string, unknown> = {
    selectedTripTypes: [],
    selectedTravelWithTypes: [],
    selectedBudget: null,
    collaborators: [],
    is_private: true,
    is_using_day_count: true,
    primary_destination: primaryDestination,
  };

  if (draft.start_date_string) {
    formData.fromDate = draft.start_date_string;
  }
  if (draft.end_date_string) {
    formData.toDate = draft.end_date_string;
  }

  const itineraryPayload = await buildItineraryPostObject({
    isEdit: false,
    editingPostId: null,
    urlPostId: null,
    remixPostObj: null,
    formData,
    tripTitle,
    actualDayCount,
    city,
    itineraryData: slots,
    timelineData,
    itineraryNotes,
    sourceMetadata: buildImportSourceMetadata(draft, sourceType, importId, importStartedAt),
    sourceSocialMediaPostId: sourceSocialMediaPostId ?? null,
    isDraft: false,
    isPrivate: true,
    keepDateStringsWithDayCount: true,
  });

  const sourceMetadata = buildImportSourceMetadata(draft, sourceType, importId, importStartedAt);
  const payload = itineraryPayload as unknown as CreatePostPayload;
  const nestedPost = (payload.post ?? {}) as Record<string, unknown>;

  return createPost({
    ...payload,
    source_metadata: sourceMetadata,
    ...(sourceSocialMediaPostId ? { source_social_media_post_id: sourceSocialMediaPostId } : {}),
    post: {
      ...nestedPost,
      source_metadata: sourceMetadata,
      ...(primaryDestination ? { primary_destination: primaryDestination } : {}),
      ...(sourceSocialMediaPostId ? { source_social_media_post_id: sourceSocialMediaPostId } : {}),
    },
  });
}

function parseCityCountryFromAddress(address: string | undefined): { city: string; country: string } {
  if (!address?.trim()) {
    return { city: '', country: '' };
  }
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return {
      city: parts[parts.length - 2] ?? '',
      country: parts[parts.length - 1] ?? '',
    };
  }
  return { city: parts[0] ?? '', country: '' };
}

export function importListNotesFromDraft(
  draft: ItineraryImportDraft,
  sourceType: ItineraryImportSourceType = 'text',
): string {
  const summary = draft.summary?.trim();
  return summary ? maybeScrubImportText(summary, sourceType) : '';
}

/** Overview / list append: summary plus day narrative from import (e.g. social reel). */
export function importAppendOverviewNotesFromDraft(
  draft: ItineraryImportDraft,
  sourceType: ItineraryImportSourceType,
): string {
  const parts: string[] = [];
  const summary = importListNotesFromDraft(draft, sourceType);
  if (summary) parts.push(summary);

  draft.days.forEach((day, index) => {
    const dayNoteParts: string[] = [];
    const labelNote = dayLabelToNote(day?.label, day.day_index ?? index);
    if (labelNote) dayNoteParts.push(labelNote);
    if (day?.notes?.trim()) {
      dayNoteParts.push(maybeScrubImportText(day.notes.trim(), sourceType));
    }
    if (dayNoteParts.length > 0) {
      parts.push(dayNoteParts.join('\n'));
    }
  });

  return parts.join('\n\n');
}

export function flattenDraftToListPlaces(
  draft: ItineraryImportDraft,
  selectedClientIds: Set<string>,
  sourceType: ItineraryImportSourceType = 'text',
): SocialImportPlace[] {
  const places: SocialImportPlace[] = [];
  let index = 0;

  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      if (!selectedClientIds.has(place.client_id)) return;

      const loc = place.location;
      const title = (place.name.trim() || loc?.name?.trim()) || '';
      if (!title) return;

      const notes = place.notes ? maybeScrubImportText(place.notes, sourceType) : undefined;

      if (loc) {
        const address = loc.address?.trim() || '';
        const { city, country } = parseCityCountryFromAddress(address);
        const imageUrls = getPhotoUrls(toPhotoIdentifiers(loc.photos));
        const placeId = loc._id || loc.google_place_id || place.client_id;
        const categoryLabel = place.category ?? loc.gt_category ?? 'Place';

        const placeCardData: PlaceCardData = {
          id: placeId,
          title,
          address,
          cityName: city,
          imageUrls,
          googlePlaceId: loc.google_place_id,
          placeId: loc.google_place_id,
          locationType: `${city || 'Unknown'} | ${categoryLabel}`,
          latitude: loc.geoposition?.coordinates?.[1],
          longitude: loc.geoposition?.coordinates?.[0],
          ...(notes ? { notes } : {}),
        };

        places.push({
          id: place.client_id || `import-place-${index}`,
          title,
          city,
          country,
          address,
          imageUrl: imageUrls[0] ?? null,
          placeCardData,
        });
      } else {
        const categoryLabel = place.category ?? 'Place';
        const placeCardData: PlaceCardData = {
          id: place.client_id,
          title,
          address: '',
          cityName: '',
          imageUrls: [],
          locationType: `Imported | ${categoryLabel}`,
          ...(notes ? { notes } : {}),
        };

        places.push({
          id: place.client_id,
          title,
          city: '',
          country: '',
          address: '',
          imageUrl: null,
          placeCardData,
        });
      }
      index += 1;
    });
  });

  return places;
}

/** All extracted rows default selected; user opt-out only via review toggles. */
export function defaultSelectedPlaceIds(draft: ItineraryImportDraft): Set<string> {
  return new Set(
    draft.days.flatMap((day) => day.places.map((place) => place.client_id)),
  );
}

export function summarizeImportReviewCounts(draft: ItineraryImportDraft): {
  totalPlaces: number;
  matchedCount: number;
  customCount: number;
  lowConfidenceCount: number;
} {
  let matchedCount = 0;
  let customCount = 0;
  let lowConfidenceCount = 0;

  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      if (!place.location || place.match_confidence === 'none') {
        customCount += 1;
      } else {
        matchedCount += 1;
        if (place.match_confidence === 'low') {
          lowConfidenceCount += 1;
        }
      }
    });
  });

  return {
    totalPlaces: matchedCount + customCount,
    matchedCount,
    customCount,
    lowConfidenceCount,
  };
}

export function formatRedactionSummary(draft: ItineraryImportDraft): string | null {
  const red = draft.redactions;
  if (!red) return null;
  const total = red.emails + red.phones + red.prices + red.booking_refs + red.names;
  if (total === 0) return null;
  return 'Some contact or booking details were removed from this import for privacy.';
}
